import type { ApiResult } from '../types';
import type { CoverNoteData } from '../types';
import type { CommonCoverNoteResponse } from '../types/coverNoteApi';
import { caseApiPdfRequest, caseApiRequest } from './apiClient';
import { parseApplicableAreasFromApi } from './criminalCaseMapper';

function strField(raw: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const v = raw[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function nullableStrField(raw: Record<string, unknown>, ...keys: string[]): string | null {
  for (const key of keys) {
    const v = raw[key];
    if (typeof v === 'string') {
      const t = v.trim();
      if (t) return t;
      return null;
    }
    if (v === null) return null;
  }
  return null;
}

function stringListOrNull(raw: Record<string, unknown>, ...keys: string[]): string[] | null {
  for (const key of keys) {
    const v = raw[key];
    if (v == null) return null;
    if (Array.isArray(v)) {
      const list = v.map((x) => (typeof x === 'string' ? x.trim() : '')).filter(Boolean);
      return list.length ? list : [];
    }
    if (typeof v === 'string') {
      const t = v.trim();
      if (!t) return [];
      // Supports comma-separated strings if API sends a single string.
      return t
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    }
  }
  return null;
}

function documentNamesFromApi(docs: unknown): string[] {
  if (!Array.isArray(docs)) return [];
  return docs
    .map((entry) => {
      if (typeof entry === 'string') return entry.trim();
      if (!entry || typeof entry !== 'object') return '';
      const row = entry as Record<string, unknown>;
      for (const key of [
        'document_type_name',
        'documentTypeName',
        'document_name',
        'documentName',
        'name',
        'label',
        'title',
        'original_file_name',
        'originalFileName',
        'file_name',
        'fileName',
      ]) {
        const value = row[key];
        if (typeof value === 'string' && value.trim()) return value.trim();
      }
      return '';
    })
    .filter(Boolean);
}

function documentsFromCoverNoteApi(raw: Record<string, unknown>): string[] {
  for (const key of [
    'documents',
    'documents_declared',
    'documentsDeclared',
    'declared_documents',
    'declaredDocuments',
  ]) {
    const value = raw[key];
    if (value == null) continue;
    const parsed = documentNamesFromApi(value);
    if (parsed.length) return parsed;
    if (Array.isArray(value) && value.length === 0) return [];
  }
  return [];
}

function applicableAreaNamesFromApi(raw: Record<string, unknown>): string[] | null {
  for (const key of ['applicableAreas', 'applicable_areas']) {
    const value = raw[key];
    if (value == null) continue;
    const { names } = parseApplicableAreasFromApi(value);
    if (names.length) return names;
    const list = stringListOrNull(raw, key);
    if (list?.length) return list;
    if (list && list.length === 0) return [];
  }
  return null;
}

export function normalizeCommonCoverNoteResponse(raw: unknown): CommonCoverNoteResponse | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;

  const nameOfAccused =
    nullableStrField(r, 'nameOfAccused', 'name_of_accused') ??
    nullableStrField(r, 'accusedOrSuspectName', 'accused_or_suspect_name');

  return {
    fileReferenceNumber: strField(r, 'fileReferenceNumber', 'file_reference_number'),
    requestSubmittedAt: strField(r, 'requestSubmittedAt', 'request_submitted_at'),
    submittedAgency: strField(r, 'submittedAgency', 'submitted_agency', 'submitted_agency_name'),
    submittedDivisionName: strField(
      r,
      'submittedDivisionName',
      'submitted_division_name',
      'submittedDivision',
      'submitted_division',
      'division_name',
      
    ),
    fileCategory: strField(r, 'fileCategory', 'file_category'),
    subFileCategory: strField(r, 'subFileCategory', 'sub_file_category'),
    applicableAreas: applicableAreaNamesFromApi(r),
    description: nullableStrField(r, 'description'),
    caseSource: nullableStrField(r, 'caseSource', 'case_source'),
    urgencyLevel: nullableStrField(r, 'urgencyLevel', 'urgency_level'),
    deadline: nullableStrField(r, 'deadline'),
    urgencyJustification: nullableStrField(r, 'urgencyJustification', 'urgency_justification'),
    connectedAgdFileReferenceNumber: nullableStrField(
      r,
      'connectedAgdFileReferenceNumber',
      'connected_agd_file_reference_number',
    ),
    accusedOrSuspectName: nameOfAccused,
    documents: documentsFromCoverNoteApi(r),
    qr: strField(r, 'qr'),
    directionOfRequest: nullableStrField(r, 'directionOfRequest', 'direction_of_request'),
    countryInvolvedName: nullableStrField(r, 'countryInvolvedName', 'country_involved_name'),
    provinceName: nullableStrField(r, 'provinceName', 'province_name'),
    districtName: nullableStrField(r, 'districtName', 'district_name'),
    externalCaseReferenceNumber: nullableStrField(
      r,
      'externalCaseReferenceNumber',
      'external_case_reference_number',
    ),
    otherExternalCaseReferenceNumber: nullableStrField(
      r,
      'otherExternalCaseReferenceNumber',
      'other_external_case_reference_number',
    ),
    caseNumber: nullableStrField(r, 'caseNumber', 'case_number'),
    nameOfAccused,
    nationalityName: nullableStrField(r, 'nationalityName', 'nationality_name'),
    passportOrNic: nullableStrField(r, 'passportOrNic', 'passport_or_nic', 'passport_or_id_number'),
    applicableTreatyOrArrangement: nullableStrField(
      r,
      'applicableTreatyOrArrangement',
      'applicable_treaty_or_arrangement',
      'applicable_treaty_arrangement',
    ),
    warrantReference: nullableStrField(r, 'warrantReference', 'warrant_reference'),
  };
}

/** Map common cover note to legacy list-modal shape (optional criminal fields left empty). */
export function commonCoverNoteToCoverNoteData(raw: CommonCoverNoteResponse): CoverNoteData {
  return {
    fileRef: raw.fileReferenceNumber,
    submittedAt: raw.requestSubmittedAt,
    agency: raw.submittedAgency || null,
    fileCategory: raw.fileCategory,
    subFileCategory: raw.subFileCategory,
    applicableAreas: raw.applicableAreas ?? [],
    description: raw.description ?? '',
    caseSource: raw.caseSource ?? '',
    urgencyLevel: raw.urgencyLevel ?? '',
    deadline: raw.deadline ?? null,
    urgencyJustification: raw.urgencyJustification ?? '',
    connectedRef: raw.connectedAgdFileReferenceNumber ?? null,
    accusedName: raw.accusedOrSuspectName ?? null,
    documents: raw.documents,
    qr: raw.qr,
  };
}

/** Merge API cover note with optional client-side fallback (empty API fields only). */
export function mergeCommonCoverNoteResponse(
  api: CommonCoverNoteResponse,
  fallback?: Partial<CommonCoverNoteResponse>,
): CommonCoverNoteResponse {
  if (!fallback) return api;

  const pickString = (primary: string | null | undefined, alt: string | null | undefined): string | null => {
    const p = primary?.trim();
    if (p) return p;
    const a = alt?.trim();
    return a || null;
  };

  const pickList = (
    primary: string[] | null | undefined,
    alt: string[] | null | undefined,
  ): string[] | null => {
    if (primary?.length) return primary;
    if (alt?.length) return alt;
    return primary ?? alt ?? null;
  };

  const pickDocuments = (primary: string[], alt?: string[]): string[] => {
    if (primary.length) return primary;
    if (alt?.length) return alt;
    return [];
  };

  return {
    fileReferenceNumber: api.fileReferenceNumber.trim() || fallback.fileReferenceNumber?.trim() || '',
    requestSubmittedAt: api.requestSubmittedAt.trim() || fallback.requestSubmittedAt?.trim() || '',
    submittedAgency: api.submittedAgency.trim() || fallback.submittedAgency?.trim() || '',
    submittedDivisionName:
      api.submittedDivisionName.trim() || fallback.submittedDivisionName?.trim() || '',
    fileCategory: api.fileCategory.trim() || fallback.fileCategory?.trim() || '',
    subFileCategory: api.subFileCategory.trim() || fallback.subFileCategory?.trim() || '',
    applicableAreas: pickList(api.applicableAreas, fallback.applicableAreas ?? null),
    description: pickString(api.description, fallback.description ?? null),
    caseSource: pickString(api.caseSource, fallback.caseSource ?? null),
    urgencyLevel: pickString(api.urgencyLevel, fallback.urgencyLevel ?? null),
    deadline: pickString(api.deadline, fallback.deadline ?? null),
    urgencyJustification: pickString(api.urgencyJustification, fallback.urgencyJustification ?? null),
    connectedAgdFileReferenceNumber: pickString(
      api.connectedAgdFileReferenceNumber,
      fallback.connectedAgdFileReferenceNumber ?? null,
    ),
    accusedOrSuspectName: pickString(api.accusedOrSuspectName, fallback.accusedOrSuspectName ?? null),
    documents: pickDocuments(api.documents, fallback.documents),
    qr: api.qr.trim() || fallback.qr?.trim() || '',
    directionOfRequest: pickString(api.directionOfRequest, fallback.directionOfRequest ?? null),
    countryInvolvedName: pickString(api.countryInvolvedName, fallback.countryInvolvedName ?? null),
    provinceName: pickString(api.provinceName, fallback.provinceName ?? null),
    districtName: pickString(api.districtName, fallback.districtName ?? null),
    externalCaseReferenceNumber: pickString(
      api.externalCaseReferenceNumber,
      fallback.externalCaseReferenceNumber ?? null,
    ),
    otherExternalCaseReferenceNumber: pickString(
      api.otherExternalCaseReferenceNumber,
      fallback.otherExternalCaseReferenceNumber ?? null,
    ),
    caseNumber: pickString(api.caseNumber, fallback.caseNumber ?? null),
    nameOfAccused: pickString(api.nameOfAccused, fallback.nameOfAccused ?? null),
    nationalityName: pickString(api.nationalityName, fallback.nationalityName ?? null),
    passportOrNic: pickString(api.passportOrNic, fallback.passportOrNic ?? null),
    applicableTreatyOrArrangement: pickString(
      api.applicableTreatyOrArrangement,
      fallback.applicableTreatyOrArrangement ?? null,
    ),
    warrantReference: pickString(api.warrantReference, fallback.warrantReference ?? null),
  };
}

/** CMS-CRC-30 — GET /v1/case-requests/cover-note/{caseRequestId} */
export async function getCommonCoverNote(
  caseRequestId: string,
): Promise<ApiResult<CommonCoverNoteResponse>> {
  const id = caseRequestId.trim();
  if (!id) return { success: false, message: 'Case request id is required.' };

  const res = await caseApiRequest<unknown>(`/cover-note/${encodeURIComponent(id)}`);
  if (!res.ok) return { success: false, message: res.message };
  const data = normalizeCommonCoverNoteResponse(res.data);
  if (!data) return { success: false, message: 'Cover note response is invalid.' };
  return { success: true, data };
}

/** CMS-CRC-31 — GET /v1/case-requests/cover-note/pdf/{caseRequestId} */
export async function downloadCommonCoverNotePdf(caseRequestId: string): Promise<ApiResult<Blob>> {
  const id = caseRequestId.trim();
  if (!id) return { success: false, message: 'Case request id is required.' };

  const res = await caseApiPdfRequest(`/cover-note/pdf/${encodeURIComponent(id)}`);
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: res.data };
}
